import { linearRegression, movingAverage, powerToDb } from './util.ts';

export type DecayMetric = 'EDT' | 'T20' | 'T30';

export interface DecayFit {
  metric: DecayMetric;
  /** Reverberation time extrapolated to a full 60 dB decay, in seconds. */
  rt60Sec: number;
  /** Coefficient of determination of the straight-line fit. Below ~0.95, distrust it. */
  r2: number;
  /** True when the decay actually reached the evaluation range above the noise floor. */
  valid: boolean;
  upperDb: number;
  lowerDb: number;
  startSec: number;
  endSec: number;
}

export interface DecayAnalysis {
  sampleRate: number;
  /** Schroeder backward-integrated decay in dB, 0 dB at the direct sound. */
  curveDb: Float64Array;
  /** Where integration stopped because the tail had sunk into the noise. */
  truncationIndex: number;
  /** Noise floor, dB relative to the direct peak's energy. */
  noiseFloorDb: number;
  /** Usable decay range: how far the curve fell before hitting the floor. */
  decayRangeDb: number;
  edt: DecayFit;
  t20: DecayFit;
  t30: DecayFit;
  /** The most trustworthy fit available: T30 if it is valid, else T20, else EDT. */
  best: DecayFit;
}

export interface DecayOptions {
  /** Index of the direct sound; integration starts here. */
  startIndex?: number;
  /** Stop integrating this many dB above the estimated noise floor. */
  noiseMarginDb?: number;
  /** Fraction of the tail used to estimate the noise floor. */
  noiseTailFraction?: number;
  /** Smoothing window for the truncation search, in seconds. */
  smoothingSec?: number;
}

/**
 * Schroeder backward integration and the reverberation times that fall out of it.
 *
 * The subtlety that decides whether the number is real: where to stop
 * integrating. Backward integration accumulates every sample to the end of the
 * recording, so once the room decay has sunk below the microphone's noise
 * floor, the integral is summing pure noise - which adds a constant, flattens
 * the tail of the curve, and biases RT60 long. So the tail is truncated a few
 * dB above the measured floor before integrating (the cheap cousin of Lundeby's
 * iterative method).
 */
export function analyzeDecay(
  ir: ArrayLike<number>,
  sampleRate: number,
  options: DecayOptions = {},
): DecayAnalysis {
  const startIndex = Math.max(0, Math.floor(options.startIndex ?? 0));
  const noiseMarginDb = options.noiseMarginDb ?? 8;
  const noiseTailFraction = options.noiseTailFraction ?? 0.1;
  const smoothingSec = options.smoothingSec ?? 0.01;

  const n = ir.length - startIndex;
  if (n < 16) throw new Error('analyzeDecay: impulse response is too short');

  const energy = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const v = ir[startIndex + i];
    energy[i] = v * v;
  }

  const peakEnergy = energy[0] > 0 ? energy[0] : Math.max(...energy);

  // Noise floor from the last slice of the capture, where the room is done.
  const tailStart = Math.max(1, Math.floor(n * (1 - noiseTailFraction)));
  let tailSum = 0;
  for (let i = tailStart; i < n; i++) tailSum += energy[i];
  const noisePower = tailSum / Math.max(1, n - tailStart);
  const noiseFloorDb = powerToDb(noisePower, peakEnergy);

  // Truncate where the smoothed instantaneous energy sinks to the floor.
  const smoothed = movingAverage(energy, Math.max(3, Math.round(smoothingSec * sampleRate)));
  const threshold = noisePower * Math.pow(10, noiseMarginDb / 10);
  let truncationIndex = n;
  for (let i = 0; i < n; i++) {
    if (smoothed[i] < threshold) {
      truncationIndex = i;
      break;
    }
  }
  truncationIndex = Math.max(Math.min(truncationIndex, n), 16);

  // Backward integration over the truncated tail.
  const curveDb = new Float64Array(truncationIndex);
  let running = 0;
  const integral = new Float64Array(truncationIndex);
  for (let i = truncationIndex - 1; i >= 0; i--) {
    running += energy[i];
    integral[i] = running;
  }
  const total = integral[0] > 0 ? integral[0] : 1;
  for (let i = 0; i < truncationIndex; i++) curveDb[i] = powerToDb(integral[i], total);

  const decayRangeDb = curveDb[truncationIndex - 1];

  const edt = fitDecay(curveDb, sampleRate, 'EDT', 0, -10);
  const t20 = fitDecay(curveDb, sampleRate, 'T20', -5, -25);
  const t30 = fitDecay(curveDb, sampleRate, 'T30', -5, -35);
  const best = t30.valid ? t30 : t20.valid ? t20 : edt;

  return { sampleRate, curveDb, truncationIndex, noiseFloorDb, decayRangeDb, edt, t20, t30, best };
}

/** Least-squares slope of the decay curve between two dB levels, scaled to 60 dB. */
function fitDecay(
  curveDb: ArrayLike<number>,
  sampleRate: number,
  metric: DecayMetric,
  upperDb: number,
  lowerDb: number,
): DecayFit {
  const from = firstIndexBelow(curveDb, upperDb);
  const to = firstIndexBelow(curveDb, lowerDb);
  const invalid: DecayFit = {
    metric, rt60Sec: NaN, r2: 0, valid: false, upperDb, lowerDb, startSec: NaN, endSec: NaN,
  };
  if (from < 0 || to < 0 || to - from < 8) return invalid;

  const count = to - from + 1;
  const xs = new Float64Array(count);
  const ys = new Float64Array(count);
  for (let i = 0; i < count; i++) {
    xs[i] = (from + i) / sampleRate;
    ys[i] = curveDb[from + i];
  }
  const { slope, r2 } = linearRegression(xs, ys);
  if (!(slope < 0)) return invalid;

  return {
    metric,
    rt60Sec: -60 / slope,
    r2,
    valid: r2 >= 0.9,
    upperDb,
    lowerDb,
    startSec: xs[0],
    endSec: xs[count - 1],
  };
}

function firstIndexBelow(curveDb: ArrayLike<number>, db: number): number {
  for (let i = 0; i < curveDb.length; i++) {
    if (curveDb[i] <= db) return i;
  }
  return -1;
}
